/**
 * difflinkerService.js
 * Service layer for the DiffLinker structure-based drug design workflow.
 *
 * Conventions match diffhopService.js (CASCADE pattern):
 *  - Auth:    Authorization: Bearer <id_token from sessionStorage>
 *  - Base:    process.env.REACT_APP_GENCHEM_DEV_API
 *  - Errors:  throw on non-2xx; callers handle display
 */

const GENCHEM_BASE = 'https://34.160.16.181.nip.io/genchem-api-dev-apigee';

function authHeaders() {
  const token = sessionStorage.getItem('id_token');
  return { Authorization: `Bearer ${token}` };
}

/**
 * Submit a DiffLinker SBDD job.
 *
 * @param {object} params
 * @param {string}  params.fragmentSource   'auto_ligand' | 'upload'
 * @param {string}  [params.pdbId]          4-char PDB ID (when pdbId flow)
 * @param {string}  params.anchorMode       'auto' | 'manual'
 * @param {string}  [params.anchorIndices]  e.g. '3,7' — required when anchorMode='manual'
 * @param {string}  params.linkerSizeMode   'auto' | 'manual'
 * @param {number}  [params.linkerSize]     3–10 — required when linkerSizeMode='manual'
 * @param {number}  params.nSamples         10–500
 * @param {string}  params.userId
 * @param {string}  params.userName
 * @param {string}  params.projectId
 * @param {string}  params.projectName
 * @param {File}    [params.pdbFile]        Required when fragmentSource='auto_ligand' + file tab
 * @param {File}    [params.fragmentsFile]  Required when fragmentSource='upload'
 *
 * @returns {Promise<{
 *   taskId: string, status: string, model_selected: string,
 *   source_type: string, chains_used: string[], fragment_count: number,
 *   n_samples: number, estimated_seconds: number, warnings: string[],
 *   ligand_source: object, fragments_sdf: string
 * }>}
 */
export async function generateLinker({
  fragmentSource,
  pdbId,
  anchorMode,
  anchorIndices,
  linkerSizeMode,
  linkerSize,
  nSamples,
  userId,
  userName,
  projectId,
  projectName,
  pdbFile,
  fragmentsFile,
}) {
  const params = new URLSearchParams({
    fragmentSource,
    anchorMode,
    linkerSizeMode,
    nSamples:    String(nSamples),
    userId:      String(userId),
    userName,
    projectId:   String(projectId),
    projectName,
  });

  if (pdbId)          params.set('pdbId', pdbId.toUpperCase());
  if (anchorMode === 'manual' && anchorIndices)
    params.set('anchorIndices', anchorIndices);
  if (linkerSizeMode === 'manual' && linkerSize !== null)
    params.set('linkerSize', String(linkerSize));

  const url = `${GENCHEM_BASE}/api/genchem/difflinker-sbdd/generate?${params.toString()}`;

  const formData = new FormData();
  if (pdbFile)       formData.append('pdbFile', pdbFile);
  if (fragmentsFile) formData.append('fragmentsFile', fragmentsFile);

  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders(),
    body: formData,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json();
}